import { useCallback, useState } from 'react'
import { Navigate, Outlet, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { getChallenges, getGuestProfile } from '../api/confetti'
import { isNotFound } from '../api/client'
import { BottomNav, RailNav } from '../components/AlbumNav'
import { GuestMenu } from '../components/GuestMenu'
import { GuestAvatar, Monogram } from '../components/Monogram'
import { plural } from '../lib/format'
import { useSession } from '../session/context'
import { useUploadQueue } from '../upload/useUploadQueue'
import { AddMediaPanel } from '../panels/AddMediaPanel'
import { EditNamePanel } from '../panels/EditNamePanel'
import { UploadStatusPanel } from '../panels/UploadStatusPanel'
import { AlbumContext, type Album } from './AlbumContext'

/**
 * De schil om de galerij en de challenges: kop met de tellingen, navigatie, en
 * de drie panelen uit het menu.
 *
 * Op 390px is de navigatie een balk onderaan en zijn de panelen schermvullend;
 * vanaf lg is de navigatie een kolom links en staat de uploadstatus als paneel
 * rechts naast de galerij. Eén boom, twee vormen.
 *
 * De panelen zitten in de querystring (`?panel=`) en niet in component-state.
 * Dat kost niets extra's en levert de terugknop van de browser op: een sheet
 * sluiten met de veeg terug is op een telefoon wat iedereen probeert.
 */
export function AlbumLayout() {
  const { slug, event, guest, saveGuest, signOut } = useSession()
  const [params, setParams] = useSearchParams()
  const [menuOpen, setMenuOpen] = useState(false)

  /**
   * De eigenaar die meekijkt in zijn eigen privé galerij.
   *
   * Hij is geen gast: hij heeft nooit een naam opgegeven, en dat hoort ook niet
   * — kijken is geen meedoen en zou hem anders in de gastentelling zetten. Alles
   * wat een gast-ik veronderstelt valt daarom weg.
   *
   * `isPrivate` hoort er met opzet bij. Zolang de galerij openstaat is de
   * eigenaar een gast als alle andere: hij uploadt mee, en dan moet hij langs
   * "hoe kennen we jou?" zoals iedereen. Zonder die voorwaarde zou hij tijdens
   * zijn eigen feest in de kijkstand belanden en zijn foto's niet kwijt kunnen.
   */
  const readOnly = event.isPrivate && event.viewerIsOwner && !guest

  /**
   * Privé is privé, en een uitgeschakelde knop op de landing houdt niemand
   * tegen die de link naar de galerij nog in zijn geschiedenis heeft staan. De
   * server weigert deze data intussen ook — dit zorgt dat je een landing te zien
   * krijgt die uitlegt waarom, in plaats van een leeg scherm.
   */
  const shutOut = event.isPrivate && !event.viewerIsOwner

  const queue = useUploadQueue(slug, guest?.guestId ?? null)

  const profile = useQuery({
    queryKey: ['guest', slug, guest?.guestId],
    queryFn: async ({ signal }) => {
      try {
        return await getGuestProfile(slug, guest!.guestId, signal)
      } catch (cause) {
        if (!isNotFound(cause)) {
          throw cause
        }

        // Het guestId uit localStorage kent de server niet (meer): een opnieuw
        // opgezette database, of een link die van een ander feest komt. Zonder
        // dit blijft elke upload op "deze gast kennen we niet" hangen, dus
        // registreren we dezelfde naam opnieuw en gaan we verder.
        const revived = await saveGuest(guest!.displayName)

        return getGuestProfile(slug, revived.guestId, signal)
      }
    },
    enabled: Boolean(guest),
  })

  const challenges = useQuery({
    queryKey: ['challenges', slug, guest?.guestId ?? null],
    queryFn: ({ signal }) => getChallenges(slug, guest?.guestId, signal),
  })

  const openPanel = useCallback(
    (panel: string | null, challengeId?: string | null) => {
      setParams(
        (current) => {
          const next = new URLSearchParams(current)

          if (panel) {
            next.set('panel', panel)
          } else {
            next.delete('panel')
          }

          // In de querystring en niet in state: dan overleeft de koppeling een
          // herlaadbeurt en kan je de link naar een challenge-upload delen.
          if (challengeId) {
            next.set('challenge', challengeId)
          } else {
            next.delete('challenge')
          }

          return next
        },
        { replace: !panel },
      )

      setMenuOpen(false)
    },
    [setParams],
  )

  const album: Album = { queue, openPanel, profile: profile.data, readOnly }

  if (shutOut) {
    // Absoluut en niet relatief: vanaf een layout-route zonder eigen pad telt
    // ".." een segment te veel weg en kom je op /f uit in plaats van op de
    // landing van dit feest.
    return <Navigate to={`/f/${slug}`} replace />
  }

  // Zonder naam kan je niets uploaden en staat er niets van jou in de tabs.
  // Ook hier absoluut: ".." telde vanaf deze layout-route een segment te veel
  // weg, dus wie zonder naam rechtstreeks naar /album ging kwam op
  // /f/wie-ben-je uit — en dat is "deze link werkt niet" in plaats van het
  // scherm dat om zijn naam vraagt.
  if (!guest && !readOnly) {
    return <Navigate to={`/f/${slug}/wie-ben-je`} replace />
  }

  const panel = params.get('panel')

  const open = challenges.data
    ? challenges.data.challenges.filter(
        (challenge) => !challenge.completedByGuest && !challenge.isExpired,
      ).length
    : 0

  const subtitle = `${plural(event.mediaCount, 'foto', 'foto’s')} · ${plural(event.guestCount, 'gast', 'gasten')}`

  return (
    <AlbumContext.Provider value={album}>
      <div className="flex h-full flex-col lg:flex-row">
        {/* --- Zijnavigatie, alleen desktop (ronde 7A) --------------------- */}
        <aside className="w-rail border-line-soft relative hidden shrink-0 flex-col border-r px-5 py-7 lg:flex">
          <div className="flex items-center gap-3 px-2 pb-6.5">
            <Monogram coupleNames={event.coupleNames} size={40} />
            <div>
              <p className="font-display text-[23px] leading-none">{event.coupleNames}</p>
              <p className="text-ink-faint mt-1 text-xs">{subtitle}</p>
            </div>
          </div>

          <RailNav counts={{ challenges: open }} />

          <div className="flex-1" />

          <div className="border-line-soft relative flex items-center gap-3 border-t pt-4">
            <GuestAvatar displayName={guest?.displayName ?? 'Eigenaar'} size={38} />

            <div className="min-w-0 flex-1">
              <p className="truncate text-[15px] font-semibold">
                {guest?.displayName ?? 'Jij, als eigenaar'}
              </p>
              <p className="text-ink-faint mt-0.5 text-xs">
                {readOnly
                  ? 'Alleen bekijken'
                  : plural(profile.data?.uploadCount ?? 0, 'upload', 'uploads')}
              </p>
            </div>

            <button
              type="button"
              aria-label="Jouw menu"
              aria-expanded={menuOpen}
              data-menu-anchor
              onClick={() => setMenuOpen((value) => !value)}
              className="border-line-warm text-ink-muted flex size-8.5 items-center justify-center rounded-full border text-[15px]"
            >
              ⚙
            </button>

            {menuOpen && (
              <GuestMenu
                profile={profile.data}
                displayName={guest?.displayName ?? 'Jij, als eigenaar'}
                readOnly={readOnly}
                anchor="bottom-left"
                onEditName={() => openPanel('naam')}
                onOpenUploads={() => openPanel('uploads')}
                onSignOut={signOut}
                onClose={() => setMenuOpen(false)}
              />
            )}
          </div>
        </aside>

        {/*
          Op mobiel schuift deze kolom, en niet de pagina erbinnen. Daardoor
          verdwijnen de kop en de filterrij zodra je scrolt en houden de foto's
          het hele scherm over — samen scheelt dat honderd pixels op een scherm
          van achthonderd. De onderbalk blijft staan omdat hij `sticky` is.

          Vanaf lg gaat het terug zoals het was: daar scrolt de pagina zelf,
          want de zijnavigatie en de kop horen daar juist wél te blijven staan.
        */}
        <div className="relative flex min-h-0 flex-1 flex-col overflow-y-auto lg:overflow-visible">
          {/*
            De actieknop op desktop hoort hier en niet in de pagina's zelf.
            Stond hij daar, dan zat hij in de galerij naast de tabs en in
            challenges naast een titel — twee plekken die net niet gelijk
            liggen, dus verspringt hij bij het wisselen. Nu blijft hij staan.
          */}
          {!readOnly && (
            <button
              type="button"
              onClick={() => openPanel('toevoegen')}
              className="bg-accent text-accent-ink shadow-accent absolute top-6.5 right-9 z-20 hidden h-13 shrink-0 cursor-pointer items-center gap-2.5 rounded-full px-6.5 text-base font-semibold transition hover:brightness-95 lg:flex"
            >
              ＋ Foto’s toevoegen
            </button>
          )}

          {/* --- Kop, alleen mobiel (ronde 6A) ---------------------------
              Eén regel. De tellingen ("14 foto's · 14 gasten") stonden hier
              onder de naam en kostten een tweede regel voor iets wat je op de
              landing al gelezen hebt en daarna niet meer nodig hebt. Op een
              telefoon is dat een band van tachtig pixels bovenaan een scherm
              dat over de foto's eronder gaat. */}
          <header className="bg-shell relative z-30 flex shrink-0 items-center justify-between px-5 pt-4 pb-2 lg:hidden">
            <div className="flex min-w-0 items-center gap-2.5">
              <Monogram coupleNames={event.coupleNames} size={30} />
              <p className="font-display truncate text-[19px] leading-none">{event.coupleNames}</p>
            </div>

            <button
              type="button"
              aria-label="Jouw menu"
              aria-expanded={menuOpen}
              data-menu-anchor
              onClick={() => setMenuOpen((value) => !value)}
            >
              <GuestAvatar
                displayName={guest?.displayName ?? 'Eigenaar'}
                size={32}
                ring={menuOpen}
              />
            </button>

            {menuOpen && (
              <GuestMenu
                profile={profile.data}
                displayName={guest?.displayName ?? 'Jij, als eigenaar'}
                readOnly={readOnly}
                anchor="top-right"
                onEditName={() => openPanel('naam')}
                onOpenUploads={() => openPanel('uploads')}
                onSignOut={signOut}
                onClose={() => setMenuOpen(false)}
              />
            )}
          </header>

          <Outlet />

          <BottomNav
            counts={{ challenges: open }}
            onAdd={readOnly ? undefined : () => openPanel('toevoegen')}
          />
        </div>

        {panel === 'uploads' && !readOnly && <UploadStatusPanel onClose={() => openPanel(null)} />}
      </div>

      {/* De drie panelen gaan allemaal over jouw eigen spullen, en die heeft de
          meekijkende eigenaar niet. Ze staan hier niet achter een uitgeschakelde
          knop maar zijn er gewoon niet: een `?panel=toevoegen` in de adresbalk
          hoort ook niets te openen. */}
      {!readOnly && (
        <>
          <AddMediaPanel
            open={panel === 'toevoegen'}
            challengeId={params.get('challenge')}
            onClose={() => openPanel(null)}
          />

          <EditNamePanel
            open={panel === 'naam'}
            uploadCount={profile.data?.uploadCount ?? 0}
            onClose={() => openPanel(null)}
          />
        </>
      )}
    </AlbumContext.Provider>
  )
}
